import axios from "axios"; 

const baseUrl = `${process.env.REACT_APP_GITO__OCPP_API_URL}`;
//const baseUrl = "http://localhost";

function getOcppErrorMessage(data, fallbackMessage) {
    if (data == null) {
        return fallbackMessage;
    }

    if (typeof data === "string") {
        const trimmed = data.trim();
        return trimmed !== "" ? trimmed : fallbackMessage;
    }

    if (typeof data.detail === "string" && data.detail.trim() !== "") {
        return data.detail;
    }

    if (typeof data.detail?.message === "string" && data.detail.message.trim() !== "") {
        return data.detail.message;
    }

    if (typeof data.errorDescription === "string" && data.errorDescription.trim() !== "") {
        return data.errorDescription;
    }

    if (typeof data.message === "string" && data.message.trim() !== "") {
        return data.message;
    }

    if (typeof data.errorCode === "string" && data.errorCode.trim() !== "") {
        return data.errorCode;
    }

    return fallbackMessage;
}

function throwOcppApiError(error, fallbackMessage) {
    if (error instanceof Error && !error.response) {
        throw error;
    }

    const message = getOcppErrorMessage(
        error?.response?.data,
        error instanceof Error ? error.message : fallbackMessage
    );
    throw new Error(message || fallbackMessage);
}

function ensureOcppStatus(data, fallbackMessage, allowedStatuses) {
    const status = data?.payload?.status;
    if (allowedStatuses.includes(status)) {
        return;
    }

    throw new Error(getOcppErrorMessage(data, fallbackMessage));
}

export async function get_LocalList_get_localList_version(apiAccessToken, chargingPointPublicId) {
    try {
        const response = await axios.get(`${baseUrl}/LocalList/get-localList-version/${chargingPointPublicId}`, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
        } });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to call GET /LocalList/get-localList-version/{chargingPointPublicId}');
    }
}

export async function post_LocalList_send_local_list(apiAccessToken, data) {
    try {
        const response = await axios.post(`${baseUrl}/LocalList/send-local-list`, data, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
        } });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to call POST /LocalList/send-local-list');
    }
}

export async function put_LocalList_change_local_list(apiAccessToken, data) {
    try {
        const response = await axios.put(`${baseUrl}/LocalList/change-local-list`, data, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
        } });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to call PUT /LocalList/change-local-list');
    }
}

export async function delete_LocalList_delete_local_list_by_local_list_public_id(apiAccessToken, localListPublicId) {
    try {
        const response = await axios.delete(`${baseUrl}/LocalList/delete-local-list/by-local-list-public-id/${localListPublicId}`, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
        } });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to call DELETE /LocalList/delete-local-list/by-local-list-public-id/{localListPublicId}');
    }
}

export async function delete_LocalList_delete_local_list_by_charging_point_public_id(apiAccessToken, chargingPointPublicId) {
    try {
        const response = await axios.delete(`${baseUrl}/LocalList/delete-local-list/by-charging-point-public-id/${chargingPointPublicId}`, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
        } });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to call DELETE /LocalList/delete-local-list/by-charging-point-public-id/{chargingPointPublicId}');
    }
}

export async function post_RemoteManagement_remote_start_charging(apiAccessToken, data) {
    const fallbackMessage = "Failed to call POST /RemoteManagement/remote-start-charging";

    try {
        const response = await axios.post(`${baseUrl}/RemoteManagement/remote-start-charging`, data, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
            }
        });
        ensureOcppStatus(response?.data, fallbackMessage, ["Accepted"]);
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function post_RemoteManagement_remote_stop_charging(apiAccessToken, data) {
    const fallbackMessage = "Failed to call POST /RemoteManagement/remote-stop-charging";

    try {
        const response = await axios.post(`${baseUrl}/RemoteManagement/remote-stop-charging`, data, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
        } });
        ensureOcppStatus(response?.data, fallbackMessage, ["Accepted"]);
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function put_RemoteManagement_change_availability(apiAccessToken, data) {
    const fallbackMessage = "Failed to call PUT /RemoteManagement/change-availability";

    try {
        const response = await axios.put(`${baseUrl}/RemoteManagement/change-availability`, data, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
        } });
        ensureOcppStatus(response?.data, fallbackMessage, ["Accepted", "Scheduled"]);
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function post_RemoteManagement_unlock_connector(apiAccessToken, data) {
    const fallbackMessage = "Failed to call POST /RemoteManagement/unlock-connector";

    try {
        const response = await axios.post(`${baseUrl}/RemoteManagement/unlock-connector`, data, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
        } });

        const res = response?.data;

        if (res?.messageType === "ErrorResponse" || res?.errorCode) {
            throw new Error(getOcppErrorMessage(res, fallbackMessage));
        }

        return res;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function post_RemoteManagement_update_firmware(apiAccessToken, data) {
    const fallbackMessage = "Failed to call POST /RemoteManagement/update-firmware";

    try {
        const response = await axios.post(`${baseUrl}/RemoteManagement/update-firmware`, data, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
        } });
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function post_RemoteManagement_reset(apiAccessToken, data) {
    const fallbackMessage = "Failed to call POST /RemoteManagement/reset";

    try {
        const response = await axios.post(`${baseUrl}/RemoteManagement/reset`, data, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${apiAccessToken}`,
            }
        });

        const res = response?.data;

        if (res?.errorCode) {
            throw new Error(getOcppErrorMessage(res, fallbackMessage));
        }

        ensureOcppStatus(res, fallbackMessage, ["Accepted"]);

        return res;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function get_RemoteManagement_get_configuration(apiAccessToken) {
    const fallbackMessage = "Failed to call GET /RemoteManagement/get-configuration";

    try {
        const response = await axios.get(`${baseUrl}/RemoteManagement/get-configuration`, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
        } });
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function put_RemoteManagement_change_configuration(apiAccessToken, data) {
    const fallbackMessage = "Failed to call PUT /RemoteManagement/change-configuration";

    try {
        const response = await axios.put(`${baseUrl}/RemoteManagement/change-configuration`, data, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
        } });
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function put_RemoteManagement_change_configurations_for_charging_points(apiAccessToken, data) {
    const fallbackMessage = "Failed to call PUT /RemoteManagement/change-configurations-for-charging-points";

    try {
        const response = await axios.put(`${baseUrl}/RemoteManagement/change-configurations-for-charging-points`, data, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
        } });
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function post_RemoteManagement_clear_cache(apiAccessToken, payload) {
    const fallbackMessage = "Failed to call POST /RemoteManagement/clear-cache";

    try {
        const response = await axios.post(`${baseUrl}/RemoteManagement/clear-cache`, payload, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
        } });
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function post_RemoteManagement_set_charging_profile(apiAccessToken, data) {
    const fallbackMessage = "Failed to call POST /RemoteManagement/set-charging-profile";

    try {
        const response = await axios.post(`${baseUrl}/RemoteManagement/set-charging-profile`, data, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
        } });
        ensureOcppStatus(response?.data, fallbackMessage, ["Accepted"]);
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function post_RemoteManagement_clear_charging_profile(apiAccessToken, data) {
    const fallbackMessage = "Failed to call POST /RemoteManagement/clear-charging-profile";

    try {
        const response = await axios.post(`${baseUrl}/RemoteManagement/clear-charging-profile`, data, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
            }
        });
        ensureOcppStatus(response?.data, fallbackMessage, ["Accepted"]);
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function post_RemoteManagement_data_transfer(apiAccessToken, data) {
    const fallbackMessage = "Failed to call POST /RemoteManagement/data-transfer";

    try {
        const response = await axios.post(`${baseUrl}/RemoteManagement/data-transfer`, data, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
        } });
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function get_RemoteManagement_get_composite_schedule(apiAccessToken) {
    const fallbackMessage = "Failed to call GET /RemoteManagement/get-composite-schedule";

    try {
        const response = await axios.get(`${baseUrl}/RemoteManagement/get-composite-schedule`, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
        } });
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function get_RemoteManagement_get_diagnostics(apiAccessToken, data) {
    const fallbackMessage = 'Failed to call GET /RemoteManagement/get-diagnostics';

    try {
        const response = await axios.get(`${baseUrl}/RemoteManagement/get-diagnostics`, { 
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${apiAccessToken}`,
            },
            params: data 
        });

        const responseData = response?.data;

        if (responseData?.messageType === "ErrorResponse" || responseData?.errorCode) {
            throw new Error(getOcppErrorMessage(responseData, fallbackMessage));
        }
        return responseData;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function post_RemoteManagement_trigger_message(apiAccessToken, data) {
    const fallbackMessage = "Failed to call POST /RemoteManagement/trigger-message";

    try {
        const response = await axios.post(`${baseUrl}/RemoteManagement/trigger-message`, data, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
        } });
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function post_Reservation_reserve_now(apiAccessToken, data) {
    try {
        const response = await axios.post(`${baseUrl}/Reservation/reserve-now`, data, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
        } });
        return response.data;
    } catch (error) {
        throw error;
    }
}

export async function get_Reservation_get_active_reservations(apiAccessToken) {
    try {
        const response = await axios.get(`${baseUrl}/Reservation/get-active-reservations`, {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
        } });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data || "Failed to call GET /Reservation/get-active-reservations");
    }
}

export async function delete_Reservation_cancel_reservation(apiAccessToken, reservationPublicId) {
    try {
        const response = await axios.delete(`${baseUrl}/Reservation/cancel-reservation/${reservationPublicId}`, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
        } });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data || 'Failed to call DELETE /Reservation/cancel-reservation/{reservationPublicId}');
    }
}

export async function get_Simulate_ping(apiAccessToken) {
    try {
        const response = await axios.get(`${baseUrl}/Simulate/ping`, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
        } });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to call GET /Simulate/ping');
    }
}

export async function post_Simulate_ocpp(apiAccessToken, data) {
    try {
        const response = await axios.post(`${baseUrl}/Simulate/ocpp`, data, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
        } });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to call POST /Simulate/ocpp');
    }
}

export async function get_Simulate_check(apiAccessToken, id) {
    try {
        const response = await axios.get(`${baseUrl}/Simulate/check/${id}`, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
        } });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to call GET /Simulate/check/{id}');
    }
}

export async function get_configurations_grouped(apiAccessToken, chargingPointPublicId) {
    const fallbackMessage = "Failed to call GET /RemoteManagement/get-configurations-grouped";

    try {
        const response = await axios.get(`${baseUrl}/RemoteManagement/get-configurations-grouped?chargingPointPublicId=${chargingPointPublicId}`, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
        } });
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}
export async function put_change_configurations(apiAccessToken, data) {
    /*
    {
  "chargingPointPublicId": "string",
  "changeConfigurations": [
    {
      "key": "string",
      "value": "string"
    }
  ]
}
  */
    const fallbackMessage = "Failed to call PUT /RemoteManagement/change-configurations";

    try {
        const response = await axios.put(`${baseUrl}/RemoteManagement/change-configurations`, data, { 
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiAccessToken}`,
        } });
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}
